<template>
  <div class="matrix-container card">
    <section class="decision-flow" aria-label="旅行先検討の流れ">
      <div class="flow-steps">
        <span class="flow-step">1 日程</span>
        <span class="flow-step">2 行先</span>
        <span class="flow-step">3 内容</span>
      </div>
      <p class="flow-summary">候補地を比較して、観光・グルメ・アクティビティ・費用の評価を確認できます。</p>
      <p class="days-summary">日程 {{ daysSummary }}</p>
    </section>

    <div class="column-config-panel">
      <button type="button" class="config-toggle-btn" :aria-expanded="isConfigOpen" aria-controls="comparison-column-config" @click="isConfigOpen = !isConfigOpen">
        <svg class="icon-inline config-icon" viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 0.3em;">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
        </svg>
        表示項目の設定 <span class="accordion-arrow" :class="{ open: isConfigOpen }">▼</span>
      </button>

      <div id="comparison-column-config" v-show="isConfigOpen" class="config-content">
        <div class="checkbox-grid">
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.budget" /> 想定予算</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.breakdown" /> 予算内訳</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.days" /> 想定日数</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.dayFit" /> 日数フィット</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.unvisited" /> 未訪問メモ</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.todo" /> できること</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.sightseeing" /> 観光</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.food" /> グルメ</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.activity" /> アクティビティ</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.cost" /> 費用</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.avgScore" /> 平均スコア</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.sightseeingAvg" /> 観光平均スコア</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.foodAvg" /> グルメ平均スコア</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.activityAvg" /> アクティビティ平均スコア</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.costAvg" /> 費用平均スコア</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.memberScores" /> メンバー評価</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.safety" /> 治安</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.bestSeason" /> ベストシーズン</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.demographics" /> 人気の層</label>
          <label class="config-checkbox-label"><input type="checkbox" v-model="visibleColumns.spots" /> おすすめ観光地</label>
        </div>
      </div>
    </div>

    <div v-if="showScrollGuide" class="scroll-guide">
      <span class="scroll-arrow">→</span>
      <span class="scroll-text">スワイプして比較</span>
      <span class="scroll-arrow">→</span>
    </div>

    <div class="matrix-scroll-wrapper" @scroll="handleScroll">
      <table class="matrix-table">
        <thead>
          <tr>
            <th scope="col" data-column-key="name" class="cand-name-cell sortable-header" :aria-sort="sortAria('name')"><button type="button" class="sort-button" @click="setSort('name')">候補地 {{ sortIndicator('name') }}</button></th>
            <th scope="col" v-if="visibleColumns.avgScore" data-column-key="avgScore" class="sortable-header" :aria-sort="sortAria('avgScore')"><button type="button" class="sort-button" @click="setSort('avgScore')">平均スコア {{ sortIndicator('avgScore') }}</button></th>
            <th scope="col" v-if="visibleColumns.sightseeingAvg" data-column-key="sightseeingAvg" class="sortable-header" :aria-sort="sortAria('sightseeingAvg')"><button type="button" class="sort-button" @click="setSort('sightseeingAvg')">観光平均スコア {{ sortIndicator('sightseeingAvg') }}</button></th>
            <th scope="col" v-if="visibleColumns.foodAvg" data-column-key="foodAvg" class="sortable-header" :aria-sort="sortAria('foodAvg')"><button type="button" class="sort-button" @click="setSort('foodAvg')">グルメ平均スコア {{ sortIndicator('foodAvg') }}</button></th>
            <th scope="col" v-if="visibleColumns.activityAvg" data-column-key="activityAvg" class="sortable-header" :aria-sort="sortAria('activityAvg')"><button type="button" class="sort-button" @click="setSort('activityAvg')">アクティビティ平均スコア {{ sortIndicator('activityAvg') }}</button></th>
            <th scope="col" v-if="visibleColumns.costAvg" data-column-key="costAvg" class="sortable-header" :aria-sort="sortAria('costAvg')"><button type="button" class="sort-button" @click="setSort('costAvg')">費用平均スコア {{ sortIndicator('costAvg') }}</button></th>
            <th scope="col" v-if="visibleColumns.breakdown" data-column-key="breakdown">予算内訳</th>
            <th scope="col" v-if="visibleColumns.budget" data-column-key="budget" class="sortable-header" :aria-sort="sortAria('budget')"><button type="button" class="sort-button" @click="setSort('budget')">想定予算 {{ sortIndicator('budget') }}</button></th>
            <th scope="col" v-if="visibleColumns.days" class="sortable-header" :aria-sort="sortAria('days')"><button type="button" class="sort-button" @click="setSort('days')">想定日数 {{ sortIndicator('days') }}</button></th>
            <th scope="col" v-if="visibleColumns.dayFit" class="sortable-header" :aria-sort="sortAria('dayFit')"><button type="button" class="sort-button" @click="setSort('dayFit')">日数フィット {{ sortIndicator('dayFit') }}</button></th>
            <th scope="col" v-if="visibleColumns.unvisited">未訪問メモ</th>
            <th scope="col" v-if="visibleColumns.todo">できること</th>
            <th scope="col" v-if="visibleColumns.sightseeing">観光</th>
            <th scope="col" v-if="visibleColumns.food">グルメ</th>
            <th scope="col" v-if="visibleColumns.activity">アクティビティ</th>
            <th scope="col" v-if="visibleColumns.cost" class="sortable-header" :aria-sort="sortAria('cost')"><button type="button" class="sort-button" @click="setSort('cost')">費用 {{ sortIndicator('cost') }}</button></th>
            <template v-if="visibleColumns.memberScores">
              <th scope="col" v-for="member in members" :key="member.MEMBER_CODE">{{ member.MEMBER_NAME }}</th>
            </template>
            <th scope="col" v-if="visibleColumns.safety" class="sortable-header" :aria-sort="sortAria('safety')"><button type="button" class="sort-button" @click="setSort('safety')">治安 {{ sortIndicator('safety') }}</button></th>
            <th scope="col" v-if="visibleColumns.bestSeason" class="sortable-header" :aria-sort="sortAria('bestSeason')"><button type="button" class="sort-button" @click="setSort('bestSeason')">ベストシーズン {{ sortIndicator('bestSeason') }}</button></th>
            <th scope="col" v-if="visibleColumns.demographics" class="sortable-header" :aria-sort="sortAria('demographics')"><button type="button" class="sort-button" @click="setSort('demographics')">人気の層 {{ sortIndicator('demographics') }}</button></th>
            <th scope="col" v-if="visibleColumns.spots" class="sortable-header" :aria-sort="sortAria('spots')"><button type="button" class="sort-button" @click="setSort('spots')">おすすめ観光地 {{ sortIndicator('spots') }}</button></th>
            <th scope="col">決定</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cand in sortedCandidates" :key="cand.CANDIDATE_NO" class="candidate-row-animate">
            <th scope="row" data-column-key="name" class="cand-name-cell">{{ cand.CANDIDATE_NAME }}</th>
            <td v-if="visibleColumns.avgScore" data-column-key="avgScore" class="score-cell">
              <span class="score-wrapper">
                <svg class="star-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                {{ getAverageScoreLabel(cand) }}
              </span>
            </td>
            <td v-if="visibleColumns.sightseeingAvg" data-column-key="sightseeingAvg">{{ getCategoryAverageLabel(cand, 'sightseeing') }}</td>
            <td v-if="visibleColumns.foodAvg" data-column-key="foodAvg">{{ getCategoryAverageLabel(cand, 'food') }}</td>
            <td v-if="visibleColumns.activityAvg" data-column-key="activityAvg">{{ getCategoryAverageLabel(cand, 'activity') }}</td>
            <td v-if="visibleColumns.costAvg" data-column-key="costAvg">{{ getCategoryAverageLabel(cand, 'cost') }}</td>
            <td v-if="visibleColumns.breakdown" data-column-key="breakdown">
              <div v-if="cand.parsedBudgetBreakdown">
                <div v-for="(val, key) in cand.parsedBudgetBreakdown" :key="key" style="font-size: 0.75rem;">
                  {{ getBudgetKeyLabel(key) }}: {{ formatCurrency(val) }}
                </div>
              </div>
              <span v-else>-</span>
            </td>
            <td v-if="visibleColumns.budget" data-column-key="budget">{{ formatCurrency(cand.ESTIMATED_BUDGET) }}</td>
            <td v-if="visibleColumns.days">{{ cand.ESTIMATED_DAYS ? cand.ESTIMATED_DAYS + '日' : '未定' }}</td>
            <td v-if="visibleColumns.dayFit">{{ getDayFit(cand) }}</td>
            <td v-if="visibleColumns.unvisited">{{ getUnvisitedMemo(cand) }}</td>
            <td v-if="visibleColumns.todo" class="summary-cell" :title="getThingsToDo(cand)">{{ getThingsToDo(cand) }}</td>
            <td v-if="visibleColumns.sightseeing" class="summary-cell" :title="getSightseeing(cand)">{{ getSightseeing(cand) }}</td>
            <td v-if="visibleColumns.food" class="summary-cell" :title="getFood(cand)">{{ getFood(cand) }}</td>
            <td v-if="visibleColumns.activity" class="summary-cell" :title="getActivity(cand)">{{ getActivity(cand) }}</td>
            <td v-if="visibleColumns.cost">{{ getCostSummary(cand) }}</td>
            <template v-if="visibleColumns.memberScores">
              <td v-for="member in members" :key="member.MEMBER_CODE">
                {{ getMemberScoreLabel(cand, member.MEMBER_CODE) }}
              </td>
            </template>
            <td v-if="visibleColumns.safety">{{ cand.parsedAiReport?.safety || '-' }}</td>
            <td v-if="visibleColumns.bestSeason">{{ cand.parsedAiReport?.bestSeason || cand.parsedAiReport?.best_season || '-' }}</td>
            <td v-if="visibleColumns.demographics">{{ cand.parsedAiReport?.targetDemographics || cand.parsedAiReport?.target_demographics || '-' }}</td>
            <td v-if="visibleColumns.spots" class="spots-cell" :title="cand.parsedAiReport?.displaySpots">{{ cand.parsedAiReport?.displaySpots || '-' }}</td>
            <td><button v-if="canFinalize" type="button" class="finalize-button" :disabled="isFinalizing" @click="$emit('finalize', cand.CANDIDATE_NO)">{{ isFinalizing ? "作成中..." : "旅行先をここにする" }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

export default {
  name: "ComparisonTable",
  props: {
    candidates: { type: Array, required: true },
    members: { type: Array, required: true },
    myMemberCode: { type: String, default: null },
    board: { type: Object, default: null },
    isFinalizing: { type: Boolean, default: false },
    canFinalize: { type: Boolean, default: false },
  },
  setup(props) {
    const isConfigOpen = ref(false);
    const showScrollGuide = ref(true);
    const sortKey = ref("avgScore");
    const sortDirection = ref("desc");
    const visibleColumns = ref({
      budget: false,
      breakdown: false,
      days: false,
      dayFit: false,
      unvisited: false,
      todo: false,
      sightseeing: false,
      food: false,
      activity: false,
      cost: false,
      avgScore: true,
      sightseeingAvg: true,
      foodAvg: true,
      activityAvg: true,
      costAvg: true,
      memberScores: false,
      safety: false,
      bestSeason: false,
      demographics: false,
      spots: false,
    });

    const handleScroll = () => {
      showScrollGuide.value = false;
    };

    const parseBudgetBreakdown = (breakdownStr) => {
      if (!breakdownStr) return null;
      try {
        if (typeof breakdownStr === "object") return breakdownStr;
        return JSON.parse(breakdownStr);
      } catch (e) {
        return null;
      }
    };

    const parseAiReport = (reportText) => {
      if (!reportText) return null;
      try {
        const parsed = JSON.parse(reportText);
        const rawSpots = parsed.recommendSpots || parsed.recommendedSpots || parsed.spots || parsed.recommend_spots;
        parsed.displaySpots = normalizeText(rawSpots);
        return parsed;
      } catch (e) {
        return { safety: reportText, bestSeason: "", displaySpots: reportText };
      }
    };

    function normalizeText(value) {
      if (!value) return "";
      if (Array.isArray(value)) {
        return value.map((item) => (item && typeof item === "object" ? item.name || item.title || item.text || JSON.stringify(item) : item))
          .filter(Boolean)
          .join(", ");
      }
      if (typeof value === "object") return value.name || value.title || value.text || Object.values(value).filter(Boolean).join(", ");
      return String(value);
    }

    function reportField(report, keys) {
      if (!report) return "";
      for (const key of keys) {
        const text = normalizeText(report[key]);
        if (text) return text;
      }
      return "";
    }

    function compactText(text, fallback = "-") {
      const normalized = String(text || "").replace(/\s+/g, " ").trim();
      return normalized || fallback;
    }

    function getRawCandidateText(candidate) {
      return [
        candidate?.MEMO_TEXT,
        candidate?.ITINERARY_SAMPLE,
        typeof candidate?.AI_RESEARCH_REPORT === "string"
          ? candidate.AI_RESEARCH_REPORT
          : JSON.stringify(candidate?.AI_RESEARCH_REPORT || ""),
      ].filter(Boolean).join(" ");
    }

    const parsedCandidates = computed(() => props.candidates.map((cand) => ({
      ...cand,
      parsedBudgetBreakdown: parseBudgetBreakdown(cand.BUDGET_BREAKDOWN),
      parsedAiReport: parseAiReport(cand.AI_RESEARCH_REPORT),
    })));

    const daysSummary = computed(() => {
      const days = parsedCandidates.value
        .map((cand) => Number(cand.ESTIMATED_DAYS || 0))
        .filter((day) => day > 0);
      if (!days.length) return "想定日数は未入力です";
      const minDays = Math.min(...days);
      const maxDays = Math.max(...days);
      return minDays === maxDays ? `${minDays}日候補` : `${minDays}〜${maxDays}日候補`;
    });

    const scoreCategories = ["sightseeing", "activity", "food", "cost"];

    function normalizeScoreDetails(value) {
      if (!value || typeof value !== "object") return null;
      const source = value.scores || value.scoreDetails || value.score_details || value;
      const details = {
        sightseeing: Number(source.sightseeingScore ?? source.sightseeing ?? source.tourism ?? source.SIGHTSEEING ?? 0) || 0,
        activity: Number(source.activityScore ?? source.activity ?? source.ACTIVITY ?? 0) || 0,
        food: Number(source.foodScore ?? source.gourmetScore ?? source.food ?? source.gourmet ?? source.FOOD ?? source.GOURMET ?? 0) || 0,
        cost: Number(source.costScore ?? source.cost ?? source.COST ?? 0) || 0,
      };
      return scoreCategories.some((key) => details[key] > 0) ? details : null;
    }


    function scoreInfo(value) {
      const details = normalizeScoreDetails(value);
      if (details) return { value: scoreCategories.reduce((sum, key) => sum + Number(details[key] || 0), 0), legacy: false };
      const raw = value && typeof value === "object" ? Number(value.SCORE_VAL ?? value.scoreVal ?? value.score ?? 0) || 0 : Number(value || 0) || 0;
      if (raw > 5) return { value: raw, legacy: false };
      return raw > 0 ? { value: raw, legacy: true } : { value: 0, legacy: false };
    }
    function scoreTotal(value) {
      return scoreInfo(value).value;
    }
    const candidateScoreValues = (cand) => {
      if (!cand) return [];
      const scoreRows = cand.scores || [];
      const memberDetailValues = Object.values(cand.memberScoreDetails || cand.member_score_details || cand.scoreDetailsByMember || cand.score_details_by_member || {});
      const memberValues = Object.values(cand.memberScores || {});
      if (memberDetailValues.some((item) => normalizeScoreDetails(item))) return memberDetailValues;
      if (scoreRows.some((item) => normalizeScoreDetails(item))) return scoreRows;
      if (memberValues.some((item) => normalizeScoreDetails(item))) return memberValues;
      if (memberDetailValues.length) return memberDetailValues;
      return scoreRows.length ? scoreRows : memberValues;
    };
    const getAverageScore = (cand) => {
      if (!cand) return 0;
      const values = candidateScoreValues(cand);
      if (!values.length) {
        return cand.averageScore !== undefined && cand.averageScore !== null ? Number(cand.averageScore) : 0;
      }
      const totals = values.map(scoreTotal).filter((score) => score > 0);
      return totals.length ? totals.reduce((sum, score) => sum + score, 0) / totals.length : 0;
    };

    const categoryAverages = (cand) => {
      const values = candidateScoreValues(cand);
      return ["sightseeing", "food", "activity", "cost"].reduce((averages, category) => {
        const scores = values
          .map((value) => normalizeScoreDetails(value)?.[category])
          .filter((value) => Number.isFinite(value) && value >= 1 && value <= 5);
        averages[category] = scores.length
          ? scores.reduce((sum, value) => sum + value, 0) / scores.length
          : null;
        return averages;
      }, {});
    };

    const rowCandidates = computed(() => parsedCandidates.value.map((candidate, originalIndex) => ({
      ...candidate,
      originalIndex,
      categoryAverages: categoryAverages(candidate),
    })));

    const getCategoryAverageLabel = (cand, category) => {
      const average = cand?.categoryAverages?.[category];
      return Number.isFinite(average) ? average.toFixed(1) + "/5" : "-";
    };


    const getAverageScoreLabel = (cand) => {
      if (!cand) return "-";
      const infos = candidateScoreValues(cand).map(scoreInfo).filter((info) => info.value > 0);
      if (infos.length) {
        const avg = infos.reduce((sum, info) => sum + info.value, 0) / infos.length;
        return infos.some((info) => !info.legacy) ? avg.toFixed(1) + "/20" : "旧評価 " + avg.toFixed(1) + "/5";
      }
      if (!cand.averageScore) return "-";
      const average = Number(cand.averageScore);
      return average > 5 ? average.toFixed(1) + "/20" : "旧評価 " + average.toFixed(1) + "/5";
    };
    const getDayFit = (cand) => (cand.ESTIMATED_DAYS ? String(cand.ESTIMATED_DAYS) + "日" : "未設定");

    const getUnvisitedMemo = (cand) => {
      const text = getRawCandidateText(cand);
      if (/(初めて|未訪問|行ったことない|行ったことがない|未経験|first time|never visited)/i.test(text)) {
        return "未訪問候補";
      }
      return "メンバーで確認";
    };

    const getSightseeing = (cand) => compactText(
      reportField(cand.parsedAiReport, ["displaySpots", "recommendSpots", "recommendedSpots", "spots", "sightseeing", "sightseeingSpots"]),
      "AI調査で確認"
    );

    const getFood = (cand) => compactText(
      reportField(cand.parsedAiReport, ["foodRecommendations", "food_recommendations", "food", "gourmet", "restaurants", "restaurantRecommendations"]),
      "AI調査で確認"
    );

    const getActivity = (cand) => compactText(
      reportField(cand.parsedAiReport, ["activities", "activity", "experiences", "experience", "thingsToDo", "things_to_do"]),
      "AI調査で確認"
    );

    const getThingsToDo = (cand) => compactText([
      getSightseeing(cand),
      getFood(cand),
      getActivity(cand),
    ].filter((text) => text && text !== "-").join(" / "), "AI調査または現地情報で確認");

    const getCostSummary = (cand) => {
      const budget = formatCurrency(cand.ESTIMATED_BUDGET);
      if (!cand.parsedBudgetBreakdown) return budget;
      const parts = Object.entries(cand.parsedBudgetBreakdown)
        .map(([key, val]) => getBudgetKeyLabel(key) + " " + formatCurrency(val));
      return parts.length ? budget + " (" + parts.join(" / ") + ")" : budget;
    };

    const sortValue = (cand, key) => {
      const values = {
        name: cand.CANDIDATE_NAME || "",
        budget: Number(cand.ESTIMATED_BUDGET || 0),
        days: Number(cand.ESTIMATED_DAYS || 0),
        dayFit: Number(cand.ESTIMATED_DAYS || 0),
        cost: Number(cand.ESTIMATED_BUDGET || 0),
        avgScore: getAverageScore(cand),
        sightseeingAvg: cand.categoryAverages?.sightseeing ?? null,
        foodAvg: cand.categoryAverages?.food ?? null,
        activityAvg: cand.categoryAverages?.activity ?? null,
        costAvg: cand.categoryAverages?.cost ?? null,
        safety: cand.parsedAiReport?.safety || "",
        bestSeason: cand.parsedAiReport?.bestSeason || cand.parsedAiReport?.best_season || "",
        demographics: cand.parsedAiReport?.targetDemographics || cand.parsedAiReport?.target_demographics || "",
        spots: cand.parsedAiReport?.displaySpots || "",
      };
      return values[key] ?? "";
    };

    const sortedCandidates = computed(() => [...rowCandidates.value].sort((a, b) => {
      const valueA = sortValue(a, sortKey.value);
      const valueB = sortValue(b, sortKey.value);
      const categorySort = ["sightseeingAvg", "foodAvg", "activityAvg", "costAvg"].includes(sortKey.value);
      if (categorySort) {
        const missingA = !Number.isFinite(valueA);
        const missingB = !Number.isFinite(valueB);
        if (missingA !== missingB) return missingA ? 1 : -1;
        if (missingA && missingB) return a.originalIndex - b.originalIndex;
      }
      const result = typeof valueA === "number" && typeof valueB === "number"
        ? valueA - valueB
        : String(valueA).localeCompare(String(valueB), "ja", { numeric: true });
      if (result === 0) return a.originalIndex - b.originalIndex;
      return sortDirection.value === "asc" ? result : -result;
    }));

    const setSort = (key) => {
      if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
        return;
      }
      sortKey.value = key;
      sortDirection.value = ["avgScore", "safety", "sightseeingAvg", "foodAvg", "activityAvg", "costAvg"].includes(key) ? "desc" : "asc";
    };

    const sortIndicator = (key) => {
      if (sortKey.value !== key) return "";
      return sortDirection.value === "asc" ? "↑" : "↓";
    };

    const sortAria = (key) => {
      if (sortKey.value !== key) return "none";
      return sortDirection.value === "asc" ? "ascending" : "descending";
    };

    const formatCurrency = (val) => {
      if (val === null || val === undefined) return "-";
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        maximumFractionDigits: 0,
      }).format(val);
    };

    const memberScoreValue = (cand, memberCode) => {
      if (!cand || !memberCode) return null;
      const detailScore = cand.memberScoreDetails?.[memberCode]
        ?? cand.member_score_details?.[memberCode]
        ?? cand.scoreDetailsByMember?.[memberCode]
        ?? cand.score_details_by_member?.[memberCode];
      if (normalizeScoreDetails(detailScore)) return detailScore;
      const scoreRow = cand.scores?.find((item) => item.MEMBER_CODE === memberCode || item.memberCode === memberCode);
      if (normalizeScoreDetails(scoreRow)) return scoreRow;
      const directScore = cand.memberScores?.[memberCode];
      if (normalizeScoreDetails(directScore)) return directScore;
      if (detailScore !== undefined && detailScore !== null) return detailScore;
      if (scoreRow) return scoreRow;
      if (directScore !== undefined && directScore !== null) return directScore;
      return null;
    };
    const getMemberScore = (cand, memberCode) => {
      const total = scoreTotal(memberScoreValue(cand, memberCode));
      return total || null;
    };


    const getMemberScoreLabel = (cand, memberCode) => {
      const info = scoreInfo(memberScoreValue(cand, memberCode));
      if (!info.value) return "-";
      return info.legacy ? "旧評価 " + info.value + "/5" : info.value + "/20";
    };
    const getBudgetKeyLabel = (key) => {
      const labels = {
        transit: "交通費",
        accommodation: "宿泊費",
        food: "食費",
        other: "その他",
      };
      return labels[key] || key;
    };

    return {
      formatCurrency,
      getAverageScore,
      getAverageScoreLabel,
      getCategoryAverageLabel,
      getMemberScore,
      getMemberScoreLabel,
      parseAiReport,
      parseBudgetBreakdown,
      getBudgetKeyLabel,
      daysSummary,
      getDayFit,
      getUnvisitedMemo,
      getThingsToDo,
      getSightseeing,
      getFood,
      getActivity,
      getCostSummary,
      parsedCandidates,
      sortedCandidates,
      isConfigOpen,
      showScrollGuide,
      visibleColumns,
      sortKey,
      sortDirection,
      setSort,
      sortIndicator,
      sortAria,
      handleScroll,
    };
  },
};
</script>

<style scoped>
.decision-flow {
  margin-bottom: 1rem;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.flow-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.flow-step {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0.25rem 0.625rem;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  color: #0f172a;
  font-size: 0.8125rem;
  font-weight: 800;
}

.flow-summary,
.days-summary {
  margin: 0;
  color: #475569;
  font-size: 0.8125rem;
  line-height: 1.6;
}

.days-summary {
  margin-top: 0.25rem;
  font-weight: 700;
}

.column-config-panel {
  margin-bottom: 1.5rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.config-toggle-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--text-main);
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s;
}

.config-toggle-btn:hover { background-color: #f1f5f9; }

.accordion-arrow {
  margin-left: auto;
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.accordion-arrow.open { transform: rotate(180deg); }

.config-content {
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  background-color: white;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
}

.config-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-main);
  cursor: pointer;
  user-select: none;
}

.config-checkbox-label input { cursor: pointer; }

.scroll-guide {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 1rem;
  color: var(--primary-hover);
  font-size: 0.8125rem;
  font-weight: 700;
  animation: pulse-guide 2s infinite;
}

.scroll-arrow {
  animation: bounce-horizontal 1s infinite alternate;
  display: inline-block;
}

@keyframes pulse-guide {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 0.6; }
}

@keyframes bounce-horizontal {
  0% { transform: translateX(-3px); }
  100% { transform: translateX(3px); }
}

.matrix-container {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
}

.matrix-scroll-wrapper { overflow-x: auto; }

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.matrix-table th,
.matrix-table td {
  padding: 1.25rem 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  font-size: 0.875rem;
  white-space: nowrap;
  color: var(--text-main);
}

.matrix-table th {
  background-color: var(--color-background);
  font-weight: 700;
  color: var(--text-muted);
  font-family: 'Space Grotesk', sans-serif;
  border-bottom: 2px solid rgba(226, 232, 240, 0.8);
}

.sortable-header {
  user-select: none;
}

.sortable-header:hover,
.sortable-header:focus-within {
  color: var(--text-main);
  background-color: #eef2f7;
}

.sort-button {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.sort-button:focus-visible {
  outline: 3px solid #1e3a5f;
  outline-offset: 4px;
  border-radius: 2px;
}

.cand-name-cell {
  font-weight: 800;
  color: var(--text-main);
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: var(--color-surface);
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
}

.matrix-table thead th.cand-name-cell {
  background-color: var(--color-background) !important;
  z-index: 3;
}

.score-cell { font-weight: 700; }

.score-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--accent-gold);
}

.star-icon {
  width: 14px;
  height: 14px;
  fill: var(--accent-gold);
}


.finalize-button {
  min-width: 9rem;
  padding: 0.5rem 0.75rem;
  border: 0;
  border-radius: 8px;
  background: var(--primary-color);
  color: white;
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 800;
}

.finalize-button:hover {
  background: var(--primary-hover);
}
.spots-cell,
.summary-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 767px) {
  .matrix-container { padding: 0.75rem !important; }
  .matrix-table th,
  .matrix-table td {
    padding: 0.75rem 0.5rem !important;
    font-size: 0.75rem !important;
  }
  .config-toggle-btn { font-size: 0.8125rem; }
  .checkbox-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
